import React from 'react';
import { useUserContext } from '../contexts/UserContext';
import FeatureLockedPage from '../pages/FeatureLockedPage';
import LoadingSpinner from './ui/LoadingSpinner';

interface FeatureGuardProps {
    featureKey: string;
    featureName: string;
    children: React.ReactNode;
}

// Features that are enabled by default if the key is missing from the plan
const CORE_FEATURES = [
    'dashboard',
    'attendance',
    'notifications',
    'plans',
    'purchases',
    'branches',
    'members',
    'team',
    'settings'
];

const FeatureGuard: React.FC<FeatureGuardProps> = ({ featureKey, featureName, children }) => {
    const { userContext, loading } = useUserContext();

    // 1. If we are still loading, show a loading state
    if (loading) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <LoadingSpinner size="lg" />
            </div>
        );
    }

    // 2. Derive active plan (Logic from Layout.tsx)
    const activeGymPlan = userContext?.roles?.find(role => role.plan)?.plan;

    // 3. If plan not fetched yet (extremely rare after loading: false), assume restricted to be safe
    if (!activeGymPlan) {
        return <FeatureLockedPage featureName={featureName} planName="unknown" />;
    }

    // 4. Check feature access
    const featureValue = activeGymPlan.features?.[featureKey];
    const isEnabled = featureValue !== undefined ? featureValue : CORE_FEATURES.includes(featureKey);

    if (!isEnabled) {
        return <FeatureLockedPage featureName={featureName} planName={activeGymPlan.name} />;
    }

    return <>{children}</>;
};

export default FeatureGuard;
